import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { characters } from '@/mocks/characters';
import type { Character } from '@/types';
import EmptyState from '@/components/base/EmptyState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function CharacterPage() {
  const { id = '' } = useParams();
  const character = (characters as Character[]).find((item) => item.id === id);
  const [activeTab, setActiveTab] = useState('growth');

  useDocumentMeta({
    title: character
      ? `${character.name} - 角色分析专题 | 善良的女秘书`
      : '角色分析专题 | 善良的女秘书',
    description: character
      ? `${character.name}，${character.role}。${character.tagline} 查看角色的性格特点、人物关系与成长轨迹深度分析。`
      : '善良的女秘书角色分析专题页。',
    keywords: character
      ? `${character.name},角色分析,${character.dramaTitle},人物关系,善良的女秘书`
      : '角色分析,善良的女秘书',
    type: 'article',
  });

  if (!character) {
    return (
      <div className="w-full px-4 py-20 md:px-8 lg:px-12">
        <EmptyState
          title="未找到该角色专题"
          description="该角色分析可能暂未收录，去首页看看其他内容吧。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/');
          }}
        />
      </div>
    );
  }

  const tabs = character.tabs;
  const currentTab = tabs.find((tab) => tab.key === activeTab) ?? tabs[0];

  return (
    <div className="w-full">
      <div className="relative h-64 w-full overflow-hidden bg-foreground-950 md:h-80">
        <img
          src={character.cover}
          alt={`${character.name} 专题主图`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-foreground-950/20"></div>
        <div className="absolute inset-x-0 bottom-0 px-4 pb-7 md:px-8 lg:px-12">
          <nav className="mb-4 flex flex-wrap items-center gap-2 text-sm text-background-300">
            <Link to="/" className="transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/detail/${character.dramaId}`}
              className="transition-colors hover:text-background-50"
            >
              {character.dramaTitle}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">角色分析</span>
          </nav>
          <div className="flex items-end gap-5">
            <span className="h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-background-50/70 bg-background-200 md:h-24 md:w-24">
              <img
                src={character.avatar}
                alt={character.name}
                className="h-full w-full object-cover object-top"
              />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
                {character.name}
              </h1>
              <p className="mt-1.5 text-sm text-background-200">{character.role}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-10 md:px-8 lg:px-12">
        <div className="mx-auto max-w-4xl">
          <p className="border-l-2 border-primary-400 pl-4 font-heading text-lg font-medium leading-relaxed text-foreground-800 md:text-xl">
            「{character.tagline}」
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {character.traits.map((trait) => (
              <span
                key={trait}
                className="rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-700"
              >
                {trait}
              </span>
            ))}
          </div>

          <section className="mt-10">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">
              角色介绍
            </h2>
            <div className="mt-4 space-y-4">
              {character.intro.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-loose text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>
        </div>

        <section className="mx-auto mt-12 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950">
            人物关系图
          </h2>
          <p className="mt-1.5 text-sm text-foreground-500">
            {character.name} 与其他主要角色之间的关系脉络。
          </p>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex flex-col items-center">
              <span className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-2 border-primary-400 bg-background-200">
                <img
                  src={character.avatar}
                  alt={character.name}
                  className="h-full w-full object-cover object-top"
                />
              </span>
              <span className="mt-2.5 font-heading text-base font-semibold text-foreground-950">
                {character.name}
              </span>
              <span className="text-xs text-foreground-500">{character.role}</span>
            </div>

            <div className="mx-auto mt-2 h-6 w-px bg-background-300"></div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {character.relations.map((relation) => (
                <div
                  key={relation.name}
                  className="relative flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3.5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-user-line"></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900">
                      {relation.name}
                    </p>
                    <p className="text-xs text-foreground-500">{relation.relation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-12 max-w-4xl">
          <div className="flex flex-wrap gap-1.5 rounded-full bg-background-200 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  currentTab.key === tab.key
                    ? 'bg-background-50 font-medium text-primary-600'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <ul className="mt-6 space-y-3">
            {currentTab.content.map((line, index) => (
              <li
                key={index}
                className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                  {index + 1}
                </span>
                <span className="text-sm leading-relaxed text-foreground-700">{line}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mx-auto mt-12 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950">
            相关影视
          </h2>
          <Link
            to={`/detail/${character.dramaId}`}
            className="group mt-4 flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-film-line text-xl"></i>
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                {character.dramaTitle}
              </h3>
              <p className="mt-0.5 text-xs text-foreground-500">
                查看完整剧情介绍与主要演员阵容
              </p>
            </div>
            <i className="ri-arrow-right-line text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-500"></i>
          </Link>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/detail/${character.dramaId}`}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
              返回影视详情
            </Link>
            <Link
              to="/category/tv"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              浏览更多影视
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}